import {TimeSpan} from "@sparkware/app-utils";
import {action, makeObservable, observable, runInAction} from "mobx";
import {generateRandomIndex} from "../../../common/utils/math-util";
import {delay} from "../../../utils/delay";
import {ServiceBase} from "../ServiceBase";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {IMagic8BallService} from "./magic-8-ball.service.interface";
import {
    InGameChallengesController_GetActiveTicketsResponse
} from "../api/api.service";

const BIG_SIZE_TIMEOUT = 5000;
const TOOLTIP_DELAY = 0;
const TOOLTIP_TIMEOUT = 5000;
const mockupPrizes = [
    ["$0", "000 free spins", "$00", "0 free play", "00 bonus"],
    ["$1", "111 free spins", "$10", "1 free play", "10 bonus"],
    ["$2", "222 free spins", "$20", "2 free play", "20 bonus"],
    ["$3", "333 free spins", "$30", "3 free play", "30 bonus"],
    ["$4", "444 free spins", "$40", "4 free play", "40 bonus"],
    ["$5", "555 free spins", "$50", "5 free play", "50 bonus"],
    ["$6", "666 free spins", "$60", "6 free play", "60 bonus"],
    ["$7", "777 free spins", "$70", "7 free play", "70 bonus"],
    ["$8", "888 free spins", "$80", "8 free play", "80 bonus"],
    ["$9", "999 free spins", "$90", "9 free play", "90 bonus"]
];

export class Magic8BallService extends ServiceBase implements IMagic8BallService {
    @observable
    progressPercentage: number = 0;
    // while showing the big size button we cannot update progressPercentage. but we dont want to loose updates so we keep them here.
    // after BIG_SIZE_TIMEOUT ms of showing the big Size button we reset the progress percentage to 0 or to this value here if it's different than null
    private progressPercentageTemp: number | null = null;
    private bigSizeTimeout: number | null = null;

    @observable
    numberOfShakes: number = 0;

    @observable
    showIntent: boolean = false;

    @observable
    bigSize: boolean = false;

    constructor(props) {
        super(props);
        makeObservable(this);
        // initialize progress and numberOfShakes from an API
    }

    isEnabled() {
        return this.services.configuration.servicesConfigs.InGameChallenges.isMagic8BallEnabled;
    }

    async attemptShowTooltip(attachElement: HTMLDivElement) {
        const tooltipWasShown = this.services.localStorage.getItem(LocalStorageKeys.tooltipShownIGC) === null;
        if (!tooltipWasShown) {
            return;
        }

        await delay(TimeSpan.fromMilliseconds(TOOLTIP_DELAY));
        const gameRunner = await this.services.gameRunner;

        const showTooltip = () => {
            this.services.regularTooltips.showTooltip({
                attachTo: attachElement,
                renderContent: () => <>
                    {this.services.translation.translate("MAGIC_8_BALL_TOOLTIP_ROW1")}<br /> 
                    {this.services.translation.translate("MAGIC_8_BALL_TOOLTIP_ROW2")}
                </>,
                timeout: TimeSpan.fromMilliseconds(TOOLTIP_TIMEOUT),
                closeOnScroll: true,
                closeOnClickOutside: true,
                zIndex: gameRunner.gameFrameZIndex + 1,
                styling: {
                    maxWidth: "45rem"
                },
                onTooltipRendered: () => {
                    this.services.localStorage.setItem(LocalStorageKeys.tooltipShownIGC, true);
                }
            });
        }

        if (gameRunner.currentRunningGame) {
            return gameRunner.currentRunningGame.executeAnimationCompleteAction({
                execute: async () => {
                    // this.services.analytics.sendOrbitUserFlowEvent(ANALYTICS_CATEGORY, 'stopCurrentGameAutoplay', `modalZIndex: ${modalZIndex}`);
                    if (gameRunner.currentRunningGame) { // We check for currentRunningGame because it possible that meanwhile the game to be closed and there is no current running game.
                        try {
                            await gameRunner.currentRunningGame.gameCommunicationChannel.stopAutoplay();
                        } catch (err) {
                            this.services.logger.error(`Failed to send stopAutoplay for game ${gameRunner.currentRunningGame.gameInfo.gameType}`);
                        }
                    }
                    showTooltip();
                }
            });
        } else {
            return showTooltip();
        }
    }

    @action
    updateProgress(newValue: number) {
        if (this.bigSizeTimeout) {
            this.progressPercentageTemp = newValue;
            return;
        }
        if (newValue < 100) {
            this.progressPercentage = newValue;
            return;
        }
        // the progress was filled, we must show the big button so that the user knows he can shake the ball
        this.progressPercentage = 100; // we show the circle around the ball fully filled
        this.numberOfShakes++; // the number of shakes increases by one
        this.bigSize = true; // show to big button
        this.bigSizeTimeout = window.setTimeout(() => {
            runInAction(() => {
                this.bigSizeTimeout = null;
                this.bigSize = false;
                if (this.progressPercentageTemp) {
                    const temp = this.progressPercentageTemp;
                    this.progressPercentageTemp = null;
                    this.updateProgress(temp % 100);
                } else {
                    this.progressPercentage = newValue % 100;
                }
            });
        }, BIG_SIZE_TIMEOUT);
    }

    @action
    decrementNumberOfShakes(): number {
        this.numberOfShakes = Math.max(this.numberOfShakes - 1, 0);
        return this.numberOfShakes;
    }

    @action
    showMagicBall() {
        this.showIntent = true;
    }

    @action
    hideMagicBall() {
        this.showIntent = false;
    }

    getPrizes(): string[] {
        return [...mockupPrizes[this.numberOfShakes % 10]];
    }
    getPrizeWonIndex() {
        if (this.numberOfShakes > 0) {
            return generateRandomIndex(mockupPrizes[this.numberOfShakes % 10]);
        } else {
            return undefined;
        }
    }

    async getTicketsAsync(): Promise<InGameChallengesController_GetActiveTicketsResponse["data"]["tickets"]> {
        return (await this.services.api.inGameChallengesController.getActiveTickets()).data.tickets;
    }
}